<template>
  <ManagerLayout>
    <div class="manager-billing">
      <h2 class="page-title">サブスク・支払い俯瞰</h2>
      <p class="page-desc">サブスク状態別件数と、直近の支払い成功・失敗一覧で請求・Stripe 周りを把握できます。</p>

      <!-- サブスク状態別件数 -->
      <section class="manager-section summary-section">
        <h3 class="section-title">サブスク状態別件数</h3>
        <div v-if="loading" class="summary-loading">読み込み中...</div>
        <div v-else class="summary-row counts-row">
          <div class="summary-card status-active">
            <span class="summary-label">active</span>
            <span class="summary-value">{{ counts.active }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card status-trialing">
            <span class="summary-label">trialing</span>
            <span class="summary-value">{{ counts.trialing }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card status-canceled">
            <span class="summary-label">canceled</span>
            <span class="summary-value">{{ counts.canceled }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card status-past_due">
            <span class="summary-label">past_due</span>
            <span class="summary-value">{{ counts.past_due }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card status-unpaid">
            <span class="summary-label">unpaid</span>
            <span class="summary-value">{{ counts.unpaid }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card status-other">
            <span class="summary-label">その他</span>
            <span class="summary-value">{{ counts.other + counts.incomplete + counts.incomplete_expired + counts.paused }}</span>
            <span class="summary-unit">件</span>
          </div>
        </div>
      </section>

      <!-- 直近の支払い成功 -->
      <section class="manager-section">
        <div class="card">
          <h3 class="section-title">直近の支払い成功</h3>
          <div class="toolbar">
            <span class="total-note">最大50件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="load">更新</button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!recentPayments.length" class="empty-state">該当データがありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="支払い成功一覧">
              <thead>
                <tr>
                  <th>日付（JST）</th>
                  <th>ユーザー</th>
                  <th class="num">金額</th>
                  <th>説明</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in recentPayments" :key="p.id" class="data-row">
                  <td>{{ formatDateTimeJst(p.createdAt) }}</td>
                  <td>
                    <span v-if="p.userFullName">{{ p.userFullName }}</span>
                    <span v-else>{{ p.userEmail || '—' }}</span>
                    <span v-if="p.userFullName && p.userEmail" class="user-email">（{{ p.userEmail }}）</span>
                    <span v-else-if="p.userEmail && !p.userFullName" class="user-email"></span>
                  </td>
                  <td class="num">{{ formatAmount(p.amount) }}</td>
                  <td class="desc-cell">{{ p.description || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- 直近の支払い失敗 -->
      <section class="manager-section">
        <div class="card">
          <h3 class="section-title">直近の支払い失敗</h3>
          <p class="section-note">決済失敗が記録されているサブスク（payment_failed=true）を表示します。</p>
          <div class="toolbar">
            <span class="total-note">{{ recentFailures.length }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="load">更新</button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!recentFailures.length" class="empty-state">該当データがありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="支払い失敗一覧">
              <thead>
                <tr>
                  <th>失敗日時（JST）</th>
                  <th>ユーザー</th>
                  <th class="num">失敗回数</th>
                  <th>次回再試行日（JST）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(f, i) in recentFailures" :key="f.userId + (f.lastPaymentFailure || '') + i" class="data-row">
                  <td>{{ formatDateTimeJst(f.lastPaymentFailure) }}</td>
                  <td>
                    <span v-if="f.userFullName">{{ f.userFullName }}</span>
                    <span v-else>{{ f.userEmail || '—' }}</span>
                    <span v-if="f.userFullName && f.userEmail" class="user-email">（{{ f.userEmail }}）</span>
                  </td>
                  <td class="num">{{ f.paymentFailureCount }} 回</td>
                  <td>{{ formatDateTimeJst(f.nextRetryDate) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const loading = ref(false)
const counts = ref({
  active: 0,
  trialing: 0,
  canceled: 0,
  past_due: 0,
  unpaid: 0,
  incomplete: 0,
  incomplete_expired: 0,
  paused: 0,
  other: 0
})
const recentPayments = ref([])
const recentFailures = ref([])

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

function formatAmount (amount) {
  if (amount == null) return '—'
  const n = Number(amount)
  if (Number.isNaN(n)) return '—'
  if (n < 0) return `- ${new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(-n)}`
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(n)
}

const load = async () => {
  loading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/billing-overview`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      counts.value = { active: 0, trialing: 0, canceled: 0, past_due: 0, unpaid: 0, incomplete: 0, incomplete_expired: 0, paused: 0, other: 0 }
      recentPayments.value = []
      recentFailures.value = []
      return
    }
    counts.value = data.subscriptionCounts || counts.value
    recentPayments.value = data.recentPayments || []
    recentFailures.value = data.recentFailures || []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  load()
})
</script>

<style scoped>
.manager-billing { padding: 0; }

.page-title { margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #1e293b; }
.page-desc { margin: 0 0 1.5rem 0; color: #64748b; font-size: 0.9rem; }
.manager-section { margin-bottom: 2rem; }
.section-title { margin: 0 0 0.75rem 0; font-size: 1.1rem; color: #334155; }
.section-note { margin: 0 0 0.75rem 0; font-size: 0.85rem; color: #64748b; }

.summary-loading { padding: 1rem; color: #64748b; }
.summary-row.counts-row { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  min-width: 100px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.summary-card.status-active { border-color: #86efac; background: #f0fdf4; }
.summary-card.status-trialing { border-color: #93c5fd; background: #eff6ff; }
.summary-card.status-canceled { border-color: #cbd5e1; background: #f8fafc; }
.summary-card.status-past_due { border-color: #fcd34d; background: #fefce8; }
.summary-card.status-unpaid { border-color: #fca5a5; background: #fef2f2; }
.summary-card.status-other { border-color: #e2e8f0; background: #f8fafc; }
.summary-label { display: block; font-size: 0.8rem; color: #64748b; margin-bottom: 0.25rem; }
.summary-value { font-size: 1.25rem; font-weight: 700; color: #1e293b; }
.summary-unit { font-size: 0.85rem; color: #64748b; margin-left: 0.25rem; }

.card { background: white; border-radius: 8px; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.total-note { font-size: 0.9rem; color: #64748b; }
.btn { padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer; border: 1px solid transparent; }
.btn-secondary { background: #e2e8f0; color: #334155; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.85rem; }
.loading-state, .empty-state { padding: 2rem; text-align: center; color: #64748b; }
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.data-table th, .data-table td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; }
.data-table th.num, .data-table td.num { text-align: right; }
.desc-cell { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-email { font-size: 0.85rem; color: #64748b; }
</style>
